import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Card, Tabs, InputField, PageLoader } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import { getAccDetails } from "../../../../utils/api";
import { HubFieldCell, HubFieldGrid, HubMutedText } from "../societyHubShell";

const Body = styled.div`
  padding: ${theme.spacing.lg};
`;

const AccountDetailsTabsRoot = styled.div`
  .account-details-tabs__list > div {
    gap: 9px;
    min-height: 42px;
    padding: 9px 6px 6px;
    align-items: center;
    box-sizing: border-box;
  }

  .account-details-tabs__btn {
    min-height: 36px;
    padding: 9px 18px;
    box-sizing: border-box;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const PanelPad = styled.div`
  padding-top: ${theme.spacing.md};
`;

const AccountDetails = ({ user }) => {
  const [activeTab, setActiveTab] = useState("virtual");
  const [bankAccountDetailDto, setBankAccountDetailDto] = useState(null);
  const [virtualAccountDetailDto, setVirtualAccountDetailDto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.orgUserDetailId && !user?.orgUserId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchAccountDetails = async () => {
      setLoading(true);
      try {
        const payload = {
          orgUserDetailId: user?.orgUserDetailId,
          orgUserId: user?.orgUserId,
        };
        const { data } = await getAccDetails(payload);
        const resData = data?.data;
        if (!cancelled) {
          setBankAccountDetailDto(resData?.bankAccountDetailDto || {});
          setVirtualAccountDetailDto(resData?.virtualAccountDetailDto || {});
        }
      } catch {
        if (!cancelled) {
          setBankAccountDetailDto({});
          setVirtualAccountDetailDto({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAccountDetails();
    return () => {
      cancelled = true;
    };
  }, [user?.orgUserDetailId, user?.orgUserId]);

  const renderFields = useCallback((fields, details) => {
    const hasData = details && fields.some((field) => details[field.key]);
    if (!hasData) {
      return <HubMutedText>No details available</HubMutedText>;
    }

    return (
      <HubFieldGrid>
        {fields.map((field) => (
          <HubFieldCell key={field.key} $span={field.width}>
            <InputField label={field.label} value={details?.[field.key] ?? ""} readOnly disabled />
          </HubFieldCell>
        ))}
      </HubFieldGrid>
    );
  }, []);

  const virtualFields = useMemo(
    () => [
      { key: "accountHolderName", label: "Account Holder Name", width: 6 },
      { key: "accountNumber", label: "Account Number", width: 6 },
      { key: "ifscCode", label: "IFSC Code", width: 6 },
      { key: "bankName", label: "Bank Name", width: 6 },
    ],
    []
  );

  const bankFields = useMemo(
    () => [
      { key: "accountHolderName", label: "Account Holder Name", width: 6 },
      { key: "accountNumber", label: "Account Number", width: 6 },
      { key: "ifscCode", label: "IFSC Code", width: 6 },
      { key: "bankName", label: "Bank Name", width: 6 },
    ],
    []
  );

  const tabs = useMemo(
    () => [
      {
        key: "virtual",
        label: "Virtual Account Details",
        content: <PanelPad>{renderFields(virtualFields, virtualAccountDetailDto)}</PanelPad>,
      },
      {
        key: "bank",
        label: "Bank Account Details",
        content: <PanelPad>{renderFields(bankFields, bankAccountDetailDto)}</PanelPad>,
      },
    ],
    [virtualFields, bankFields, virtualAccountDetailDto, bankAccountDetailDto, renderFields]
  );

  if (loading) {
    return (
      <LoaderWrap>
        <PageLoader />
      </LoaderWrap>
    );
  }

  return (
    <Card style={{ overflow: "hidden" }}>
      <Body>
        <AccountDetailsTabsRoot>
          <Tabs
            variant="underline"
            activeKey={activeTab}
            onChange={(key) => setActiveTab(String(key))}
            tabs={tabs}
            tabListClassName="account-details-tabs__list"
            tabClassName="account-details-tabs__btn"
          />
        </AccountDetailsTabsRoot>
      </Body>
    </Card>
  );
};

export default AccountDetails;
